<template>
    <RegisterStep
        :ready="ready"
        :previous-route="previousRoute"
        :submit-label="submitButtonLabel"
        v-bind="$attrs"
        @submit="handleStepSubmitted"
    >
        <template v-if="hasAccount">
            <div class="cursus-summary__section">
                <h3 class="cursus-summary__title">Coordonnées</h3>

                <div class="list-group">
                    <template v-if="selectedAccount">
                        <AccountItem
                            class="py-3"
                            :account="selectedAccount"
                            :selectable="false"
                            :username="user.full_name"
                        />
                    </template>
                </div>

                <template v-if="canEditAccount">
                    <div class="mt-3">
                        <router-link :to="{ name: 'register-account' }" custom v-slot="{ href, navigate }">
                            <a class="btn btn-link btn-sm" :href="href" @click="navigate">
                                modifier les coordonnées&nbsp;&nbsp;&gt;
                            </a>
                        </router-link>
                    </div>
                </template>
            </div>
        </template>

        <template v-if="hasRegistrants">
            <div class="cursus-summary__section">
                <h3 class="cursus-summary__title">Inscrits</h3>

                <SummaryCards :order="order" v-slot="{ registrant }">
                    <template v-if="registrant.email">
                        <div>{{ registrant.email }}</div>
                    </template>
                    <template v-else>
                        <em class="text-muted" style="font-size: .875rem">
                            Cet inscrit ne sera pas invité à créer un compte
                        </em>
                    </template>
                </SummaryCards>

                <template v-if="canEditRegistrants">
                    <div class="mt-3">
                        <router-link :to="{ name:'register-registrants' }" custom v-slot="{ href, navigate }">
                            <a class="btn btn-link btn-sm" :href="href" @click="navigate">
                                modifier les inscrits&nbsp;&nbsp;&gt;
                            </a>
                        </router-link>
                    </div>
                </template>
            </div>
        </template>

        <template v-if="hasOptions">
            <div class="cursus-summary__section">
                <h3 class="cursus-summary__title">Options</h3>

                <SummaryCards :order="order" v-slot="{ registrant }">
                    <RegistrantOptions
                        :registrant="registrant"
                        :session="session"
                    />
                </SummaryCards>

                <template v-if="canEditOptions">
                    <div class="mt-3">
                        <router-link :to="{ name:'register-options' }" custom v-slot="{ href, navigate }">
                            <a class="btn btn-link btn-sm" :href="href" @click="navigate">
                                modifier les options&nbsp;&nbsp;&gt;
                            </a>
                        </router-link>
                    </div>
                </template>
            </div>
        </template>

        <template v-if="hasForm">
            <div class="cursus-summary__section">
                <h3 class="cursus-summary__title">Formulaire</h3>

                <template v-if="order.third_party">
                    <div class="row mx-n2 mb-n3">
                        <template v-for="registrant in order.registrants">
                            <div class="col-md-6 px-2 mb-3">
                                <RegistrantCard :order="order" :registrant="registrant">
                                    <FormojAnswer
                                        class="m-0"
                                        :answer-id="registrant.registration_answer_id"
                                        appearance="default"
                                    />
                                </RegistrantCard>
                            </div>
                        </template>
                    </div>
                </template>
                <template v-else>
                    <FormojAnswer class="m-0" :answer-id="order.registrants[0].registration_answer_id" />
                </template>

                <template v-if="canEditForm">
                    <div class="mt-3">
                        <router-link :to="{ name:'register-form' }" custom v-slot="{ href, navigate }">
                            <a class="btn btn-link btn-sm" :href="href" @click="navigate">
                                modifier les informations du formulaire&nbsp;&nbsp;&gt;
                            </a>
                        </router-link>
                    </div>
                </template>
            </div>
        </template>

        <template v-if="hasConfirmationForm">
            <div class="cursus-summary__section">
                <h3 class="cursus-summary__title">Formulaire de confirmation</h3>

                <FormojAnswer
                    class="m-0"
                    :answer-id="order.registrants[0].confirmation_answer_id"
                />

                <template v-if="canEditConfirmationForm">
                    <div class="mt-3">
                        <router-link :to="{ name:'confirmation-form' }" custom v-slot="{ href, navigate }">
                            <a class="btn btn-link btn-sm" :href="href" @click="navigate">
                                modifier les informations du formulaire&nbsp;&nbsp;&gt;
                            </a>
                        </router-link>
                    </div>
                </template>
            </div>
        </template>

        <template v-if="hasPrices">
            <div class="cursus-summary__section">
                <h3 class="cursus-summary__title">Tarifs</h3>

                <SummaryCards :order="order" v-slot="{ registrant }">
                    <RegistrantPrice
                        :registrant="registrant"
                        :course="course"
                    />
                </SummaryCards>

                <template v-if="canEditPrices">
                    <div class="mt-3">
                        <router-link :to="{ name:'register-prices' }" custom v-slot="{ href, navigate }">
                            <a class="btn btn-link btn-sm" :href="href" @click="navigate">
                                modifier le tarif&nbsp;&nbsp;&gt;
                            </a>
                        </router-link>
                    </div>
                </template>
            </div>
        </template>

        <template v-if="hasMeals">
            <div class="cursus-summary__section">
                <h3 class="cursus-summary__title">Options repas</h3>

                <SummaryCards :order="order" v-slot="{ registrant }">
                    <RegistrantMeals
                        :registrant="registrant"
                        :session="session"
                    />
                </SummaryCards>

                <template v-if="canEditMeals">
                    <div class="mt-3">
                        <router-link :to="{ name:'register-meals' }" custom v-slot="{ href, navigate }">
                            <a class="btn btn-link btn-sm" :href="href" @click="navigate">
                                modifier les options repas&nbsp;&nbsp;&gt;
                            </a>
                        </router-link>
                    </div>
                </template>
            </div>
        </template>

        <template v-if="hasCheckout">
            <div class="cursus-summary__section">
                <h3 class="cursus-summary__title">Transaction</h3>

                <div class="card">
                    <div class="card-body">
                        <CheckoutInformations
                            :order="order"
                        />
                    </div>
                </div>

                <template v-if="canEditCheckout">
                    <div class="mt-3">
                        <router-link :to="{ name:'register-checkout' }" custom v-slot="{ href, navigate }">
                            <a class="btn btn-link btn-sm" :href="href" @click="navigate">
                                modifier les informations de paiement&nbsp;&nbsp;&gt;
                            </a>
                        </router-link>
                    </div>
                </template>
            </div>
        </template>

        <template v-if="hasTotal">
            <div class="mb-4">
                <h3 class="cursus-summary__title mb-0">Total : <strong>{{ order.current_total_formatted }}</strong></h3>
            </div>
        </template>

        <template v-if="hasTerms" v-slot:append>
            <b-form-checkbox v-model="currentOrder.cgv_accepted" @change="handleChanged">
                J’accepte les Conditions générales de vente et de participation
                (<a :href="baseUrl('/cursus/cgv')" target="_blank">voir</a>)
            </b-form-checkbox>

            <b-form-checkbox v-model="currentOrder.rules_accepted" @change="handleChanged">
                J’ai lu le règlement intérieur
                (<a :href="baseUrl('/cursus/reglement-interieur')" target="_blank">voir</a>)
            </b-form-checkbox>
        </template>
    </RegisterStep>
</template>

<script>
    import { clone } from "../../../util";
    import { FormojAnswer } from 'formoj';
    import { BFormCheckbox } from 'bootstrap-vue';
    import RegisterStep from "../RegisterStep.vue";
    import AccountItem from "../../AccountItem.vue";
    import { getAccount } from "../../../api";
    import {
        hasOptionsStep,
        hasFormStep,
        hasPricesStep,
        hasMealsStep,
        hasCheckoutStep,
        hasAccountStep,
        hasRegistrantsStep,
        hasPrices,
        hasMeals,
        hasCheckout,
        hasAccount,
        hasRegistrants,
        hasOptions,
        hasForm, hasConfirmationFormStep, hasConfirmationForm,
    } from "../../../util/register";
    import { baseUrl } from "../../../util/config";
    import SummaryCards from "./summary/SummaryCards.vue";
    import RegistrantPrice from "./summary/RegistrantPrice.vue";
    import RegistrantOptions from "./summary/RegistrantOptions.vue";
    import RegistrantMeals from "./summary/RegistrantMeals.vue";
    import CheckoutInformations from "./summary/CheckoutInformations.vue";
    import Icon from "../../ui/Icon.vue";
    import RegistrantCard from "../RegistrantCard.vue";
    import Field from "../../Field.vue";

    export default {
        components: {
            Field,
            BFormCheckbox,
            RegistrantCard,
            Icon,
            CheckoutInformations,
            RegistrantMeals,
            RegistrantOptions,
            RegistrantPrice,
            SummaryCards,
            AccountItem,
            RegisterStep,
            FormojAnswer,
        },
        props: {
            order: Object,
            course: Object,
            session: Object,
            previousRoute: Object,
            user: Object,
        },
        data() {
            return {
                ready: false,
                currentOrder: clone({
                    ...this.order,
                    cgv_accepted: false,
                    rules_accepted: false,
                }),
                selectedAccount: null,
            }
        },
        computed: {
            submitButtonLabel() {
                if(this.order?.is_quote) {
                    return 'Obtenir le devis';
                }
                return this.course.registration_level === 'APPLICATION'
                    ? 'Valider la candidature'
                    : `Valider l’inscription`;
            },

            /** Account */
            hasAccount() {
                return hasAccount({ });
            },
            canEditAccount() {
                return hasAccountStep({ order:this.order });
            },

            /** Registrants */
            hasRegistrants() {
                return hasRegistrants({ order:this.order });
            },
            canEditRegistrants() {
                return hasRegistrantsStep({ order:this.order })
            },

            /** Options */
            hasOptions() {
                return hasOptions({ session:this.session, order:this.order });
            },
            canEditOptions() {
                return hasOptionsStep({ session:this.session, order:this.order });
            },

            /** Form */
            hasForm() {
                return hasForm({ course:this.course, order:this.order });
            },
            canEditForm() {
                return hasFormStep({ course:this.course, order:this.order });
            },

            /** Form */
            hasConfirmationForm() {
                return hasConfirmationForm({ course:this.course, order:this.order });
            },
            canEditConfirmationForm() {
                return hasConfirmationFormStep({ course:this.course, order:this.order });
            },

            /** Prices */
            hasPrices() {
                return hasPrices({ course:this.course, order:this.order });
            },
            canEditPrices() {
                return hasPricesStep({ course:this.course, order:this.order });
            },

            /** Meals */
            hasMeals() {
                return hasMeals({ course:this.course, session:this.session, order:this.order });
            },
            canEditMeals() {
                return hasMealsStep({ course:this.course, session:this.session, order:this.order });
            },

            /** Checkout */
            hasCheckout() {
                return hasCheckout({ course:this.course, session:this.session, order:this.order });
            },
            canEditCheckout() {
                return hasCheckoutStep({ course:this.course, session:this.session, order:this.order });
            },

            hasTotal() {
                return this.hasCheckout || this.order.is_quote;
            },

            hasTerms() {
                return !this.order.is_quote;
            },
        },
        methods: {
            baseUrl,
            handleStepSubmitted() {
                this.$emit('confirm', this.order);
            },
            handleChanged() {
                this.$emit('update:order', {
                    ...this.order,
                    ...this.currentOrder,
                });
            },
            async init() {
                this.selectedAccount = await getAccount({ accountId:this.order.account_id })
                    .catch(error => {});
                this.ready = true;
            },
        },
        created() {
            this.init();
        }
    }
</script>
